// Offertes en Facturen: sluit de geldlus tussen CRM, intakegesprek, betaling en boekhouding.
//   lead -> offerte (nummer O-jjjj-nnn) -> geaccepteerd -> lead wordt gewonnen (database-trigger, start de oplevering)
//   offerte -> factuur (nummer F-jjjj-nnn) in admin_docs + admin_transactions (komt vanzelf in BTW en Boekhouder)
//   factuur -> Mollie-betaallink -> betaald (webhook) -> activiteit bij de klant en herinneringstaak dicht
// Er wordt niets automatisch naar klanten gestuurd: Furgel downloadt de PDF en verstuurt zelf.
import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../supabase";
import { laadCatalogus } from "../intake/pakketten";
import {
  C, eur, afr, vandaagIso, plusDagen, datumNl, kwartaal, dagenTot, leadNaam, echtBedrijf, lees, apiFetch,
  Kop, Stat, Chip, Melding, MigratieMelding, Venster, Veld, berekenTotalen, openAfdruk, Rij, Leeg,
} from "./gedeeld";

const OFFERTE_KLEUR = { concept: C.muted, verstuurd: C.blue, geaccepteerd: C.green, afgewezen: C.red, verlopen: C.yellow };
const LEGE_REGEL = { omschrijving: "", aantal: 1, prijs: 0, maand: false };

function factuurStatus(d) {
  if (d.payment_status === "betaald") return { tekst: "Betaald", kleur: C.green };
  if (d.vervaldatum && dagenTot(d.vervaldatum) < 0) return { tekst: "Te laat (" + -dagenTot(d.vervaldatum) + " d)", kleur: C.red };
  if (d.payment_status === "pending") return { tekst: "Betaling gestart", kleur: C.blue };
  return { tekst: "Open", kleur: C.yellow };
}

export default function Facturatie({ user, openKlant, startLeadId, wisStartLead }) {
  const [tab, setTab] = useState("offertes");
  const [leads, setLeads] = useState([]);
  const [offertes, setOffertes] = useState([]);
  const [facturen, setFacturen] = useState([]);
  const [catalogus, setCatalogus] = useState([]);
  const [ontbreekt, setOntbreekt] = useState(false);
  const [laden, setLaden] = useState(true);
  const [bewerk, setBewerk] = useState(null);
  const [bericht, setBericht] = useState(null);
  const [bezig, setBezig] = useState(false);

  const door = (user && user.email) || "furgel@dif3r3nt.nl";
  const toon = (tekst, type = "ok") => { setBericht({ tekst, type }); setTimeout(() => setBericht(null), 6000); };

  const laad = useCallback(async () => {
    const [l, o, f] = await Promise.all([
      lees(supabase.from("leads").select("id,name,company,email,segment,status,value").order("created_at", { ascending: false })),
      lees(supabase.from("crm_offertes").select("*").order("created_at", { ascending: false })),
      lees(supabase.from("admin_docs").select("*").eq("doc_type", "factuur_verkoop").order("doc_date", { ascending: false })),
    ]);
    setLeads(l.data); setOffertes(o.data); setFacturen(f.data);
    setOntbreekt(o.ontbreekt || f.ontbreekt);
    setLaden(false);
  }, []);

  useEffect(() => {
    laad();
    laadCatalogus(supabase).then((c) => setCatalogus(c.pakketten || [])).catch(() => {});
    const ch = supabase.channel("facturatie_sync")
      .on("postgres_changes", { event: "*", schema: "public", table: "crm_offertes" }, () => laad())
      .on("postgres_changes", { event: "*", schema: "public", table: "admin_docs" }, () => laad())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [laad]);

  const leadMap = useMemo(() => new Map(leads.map((l) => [String(l.id), l])), [leads]);
  const lead = (id) => leadMap.get(String(id));

  function nieuweOfferte(leadId) {
    setTab("offertes");
    setBewerk({ id: null, lead_id: leadId || "", titel: "", regels: [{ ...LEGE_REGEL }], btw_pct: 21, geldig_tot: plusDagen(vandaagIso(), 30), notities: "Betaling binnen 14 dagen na factuurdatum." });
  }

  // Doorverwijzing vanuit CRM of Klantdossier: meteen een nieuwe offerte voor die klant
  useEffect(() => {
    if (startLeadId) { nieuweOfferte(String(startLeadId)); if (wisStartLead) wisStartLead(); }
  }, [startLeadId]);

  async function bewaarOfferte() {
    const o = bewerk;
    if (!o.lead_id) { toon("Kies eerst een klant of lead.", "fout"); return; }
    if (!o.titel.trim()) { toon("Geef de offerte een titel.", "fout"); return; }
    const regels = o.regels.filter((r) => r.omschrijving.trim()).map((r) => ({ ...r, aantal: Number(r.aantal) || 0, prijs: Number(r.prijs) || 0 }));
    if (!regels.length) { toon("Voeg minstens een regel toe.", "fout"); return; }
    setBezig(true);
    const t = berekenTotalen(regels, o.btw_pct);
    const rij = { lead_id: o.lead_id, titel: o.titel.trim(), regels, btw_pct: Number(o.btw_pct) || 0, totaal_excl: t.excl, totaal_incl: t.incl, geldig_tot: o.geldig_tot || null, notities: o.notities || null };
    let fout;
    if (o.id) {
      ({ error: fout } = await supabase.from("crm_offertes").update(rij).eq("id", o.id));
    } else {
      const { data: nr, error: e1 } = await supabase.rpc("volgend_nummer", { p_soort: "offerte" });
      if (e1) { toon("Nummer ophalen mislukt: " + e1.message, "fout"); setBezig(false); return; }
      ({ error: fout } = await supabase.from("crm_offertes").insert([{ ...rij, nummer: nr, created_by: door }]));
    }
    setBezig(false);
    if (fout) { toon("Opslaan mislukt: " + fout.message, "fout"); return; }
    setBewerk(null); toon("Offerte opgeslagen."); laad();
  }

  async function zetStatus(o, status) {
    setBezig(true);
    const { error } = await supabase.from("crm_offertes").update({ status }).eq("id", o.id);
    if (error) { toon("Status wijzigen mislukt: " + error.message, "fout"); setBezig(false); return; }
    // Doorlopende regels (zorgpakketten) worden bij acceptatie meteen een abonnement bij de klant
    if (status === "geaccepteerd") {
      const maandRegels = (o.regels || []).filter((r) => r.maand);
      if (maandRegels.length) {
        const { data: bestaand } = await supabase.from("klant_diensten").select("dienst").eq("lead_id", o.lead_id);
        const heeft = new Set((bestaand || []).map((x) => x.dienst));
        const nieuw = maandRegels.filter((r) => !heeft.has(r.omschrijving)).map((r) => ({
          lead_id: o.lead_id, dienst: r.omschrijving, bedrag_maand: afr((Number(r.aantal) || 0) * (Number(r.prijs) || 0)), btw_pct: o.btw_pct, start: vandaagIso(), status: "actief",
        }));
        if (nieuw.length) await supabase.from("klant_diensten").insert(nieuw);
      }
    }
    setBezig(false);
    toon(status === "geaccepteerd" ? "Geaccepteerd: de lead is gewonnen en de opleveringstaken staan klaar." : "Status bijgewerkt.");
    laad();
  }

  async function maakFactuur(o) {
    if (o.factuur_doc_id) { toon("Deze offerte heeft al een factuur.", "let-op"); return; }
    const l = lead(o.lead_id);
    const regels = (o.regels || []).filter((r) => !r.maand);
    const t = berekenTotalen(regels, o.btw_pct);
    if (t.excl <= 0) { toon("Deze offerte heeft geen eenmalige regels om te factureren.", "let-op"); return; }
    if (!window.confirm("Factuur maken voor " + eur(t.incl) + " (incl. btw) aan " + leadNaam(l) + "?")) return;
    setBezig(true);
    const { data: nr, error: e1 } = await supabase.rpc("volgend_nummer", { p_soort: "factuur" });
    if (e1) { toon("Nummer ophalen mislukt: " + e1.message, "fout"); setBezig(false); return; }
    const datum = vandaagIso();
    const q = kwartaal(datum);
    const jaar = new Date(datum + "T12:00:00").getFullYear();
    const doc = {
      uploaded_by: "furgel", doc_type: "factuur_verkoop", status: "processed", doc_date: datum, customer: leadNaam(l),
      description: o.titel + " (" + (o.nummer || "") + ")", amount_excl: t.excl, vat_rate: Number(o.btw_pct) || 0, vat_amount: t.btw, amount_incl: t.incl,
      category: "Omzet", quarter: q, year: jaar, flagged: false, payment_status: "open",
      lead_id: o.lead_id, factuur_nummer: nr, vervaldatum: plusDagen(datum, 14), regels, offerte_id: o.id,
    };
    const { data: d, error: e2 } = await supabase.from("admin_docs").insert([doc]).select();
    if (e2 || !d) { toon("Factuur opslaan mislukt: " + (e2 && e2.message), "fout"); setBezig(false); return; }
    const { error: e3 } = await supabase.from("admin_transactions").insert([{
      trans_date: datum, type: "income", category: "Omzet", description: doc.description, amount_excl: t.excl, vat_rate: doc.vat_rate,
      vat_amount: t.btw, amount_incl: t.incl, doc_id: d[0].id, year: jaar, quarter: q,
    }]);
    await supabase.from("crm_offertes").update({ factuur_doc_id: d[0].id }).eq("id", o.id);
    await supabase.from("activities").insert([{ lead_id: o.lead_id, type: "Factuur", icon: "€", text: "Factuur " + nr + " gemaakt (" + eur(t.incl) + ")", time: "Zojuist" }]);
    setBezig(false);
    toon(e3 ? "Factuur " + nr + " gemaakt, maar de boeking mislukte: " + e3.message : "Factuur " + nr + " gemaakt en geboekt als omzet.", e3 ? "let-op" : "ok");
    setTab("facturen"); laad();
  }

  async function betaalLink(f) {
    const bedrag = Number(f.amount_incl) || Number(f.amount_excl) || 0;
    if (bedrag <= 0) { toon("Geen bedrag op deze factuur.", "fout"); return; }
    setBezig(true);
    try {
      const r = await apiFetch("/api/mollie-create-payment", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount: bedrag, description: "Factuur " + (f.factuur_nummer || "") + " Dif3r3nt", docId: f.id, redirectBase: window.location.origin }),
      });
      const d = await r.json();
      if (!r.ok || !d.checkoutUrl) { toon("Betaallink mislukt: " + (d.error || "onbekend"), "fout"); setBezig(false); return; }
      await supabase.from("admin_docs").update({ payment_status: "pending", mollie_payment_id: d.id, payment_url: d.checkoutUrl }).eq("id", f.id);
      toon("Betaallink gemaakt. Je vindt hem in de PDF en kunt hem kopieren.");
      laad();
    } catch (e) { toon("Betaallink mislukt: " + (e.message || "onbekend"), "fout"); }
    setBezig(false);
  }

  async function markeerBetaald(f) {
    if (!window.confirm("Factuur " + f.factuur_nummer + " handmatig als betaald markeren (bijvoorbeeld bij bankoverschrijving)?")) return;
    const { error } = await supabase.from("admin_docs").update({ payment_status: "betaald", paid_at: new Date().toISOString() }).eq("id", f.id);
    if (error) toon("Mislukt: " + error.message, "fout"); else { toon("Gemarkeerd als betaald."); laad(); }
  }

  function afdrukOfferte(o) {
    const l = lead(o.lead_id) || {};
    const gelukt = openAfdruk({ soort: "Offerte", nummer: o.nummer, datum: datumNl(o.created_at), geldigTot: datumNl(o.geldig_tot), klant: { naam: l.name, bedrijf: echtBedrijf(l), email: l.email }, titel: o.titel, regels: o.regels, btwPct: o.btw_pct, notities: o.notities });
    if (!gelukt) toon("Sta pop-ups toe om de PDF te openen.", "let-op");
  }
  function afdrukFactuur(f) {
    const l = lead(f.lead_id) || { name: f.customer };
    const gelukt = openAfdruk({ soort: "Factuur", nummer: f.factuur_nummer, datum: datumNl(f.doc_date), vervalt: datumNl(f.vervaldatum), klant: { naam: l.name || f.customer, bedrijf: echtBedrijf(l), email: l.email }, titel: f.description, regels: f.regels || [], btwPct: f.vat_rate, notities: "", betaalUrl: f.payment_status === "betaald" ? "" : f.payment_url });
    if (!gelukt) toon("Sta pop-ups toe om de PDF te openen.", "let-op");
  }

  const openstaand = facturen.filter((f) => f.payment_status !== "betaald");
  const teLaat = openstaand.filter((f) => f.vervaldatum && dagenTot(f.vervaldatum) < 0);
  const lopend = offertes.filter((o) => o.status === "verstuurd");
  const jaar = String(new Date().getFullYear());

  return (
    <div>
      <Kop titel="Offertes en Facturen" sub="Van intakegesprek tot betaling. Alles landt vanzelf in het klantdossier, de taken en de boekhouding."
        rechts={<button className="btn bp" onClick={() => nieuweOfferte("")}>+ Nieuwe offerte</button>} />
      {ontbreekt && <MigratieMelding />}
      {bericht && <Melding type={bericht.type}>{bericht.tekst}</Melding>}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 10, marginBottom: 16 }}>
        <Stat label="Openstaand" waarde={eur(openstaand.reduce((s, f) => s + (Number(f.amount_incl) || 0), 0))} sub={openstaand.length + " facturen"} />
        <Stat label="Te laat" waarde={teLaat.length} kleur={teLaat.length ? C.red : C.green} sub={teLaat.length ? eur(teLaat.reduce((s, f) => s + (Number(f.amount_incl) || 0), 0)) : "Niets achterstallig"} />
        <Stat label="Offertes in de lucht" waarde={lopend.length} sub={eur(lopend.reduce((s, o) => s + (Number(o.totaal_excl) || 0), 0)) + " ex btw"} />
        <Stat label="Betaald dit jaar" waarde={eur(facturen.filter((f) => f.payment_status === "betaald" && String(f.doc_date).startsWith(jaar)).reduce((s, f) => s + (Number(f.amount_incl) || 0), 0))} kleur={C.green} />
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
        <span className={"tab" + (tab === "offertes" ? " on" : "")} onClick={() => setTab("offertes")}>Offertes ({offertes.length})</span>
        <span className={"tab" + (tab === "facturen" ? " on" : "")} onClick={() => setTab("facturen")}>Facturen ({facturen.length})</span>
      </div>

      {laden && <p style={{ color: C.muted, fontSize: 13 }}>Laden...</p>}

      {tab === "offertes" && !laden && (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          {offertes.length === 0 && <Leeg tekst={'Nog geen offertes. Maak er een vanuit een lead, of kies "Nieuwe offerte".'} />}
          {offertes.map((o) => {
            const l = lead(o.lead_id);
            return (
              <Rij key={o.id} titel={<>{o.titel} <span style={{ color: C.muted, fontWeight: 400 }}>{o.nummer}</span></>}
                rechts={<span style={{ display: "inline-flex", gap: 4, flexWrap: "wrap", justifyContent: "flex-end" }}><Chip tekst={o.status} kleur={OFFERTE_KLEUR[o.status]} />{o.factuur_doc_id && <Chip tekst="gefactureerd" kleur={C.green} />}</span>}
                meta={<>
                  <span style={{ cursor: l ? "pointer" : "default", color: l ? C.blue : C.muted }} onClick={(e) => { e.stopPropagation(); if (l && openKlant) openKlant(l.id); }}>{leadNaam(l) || "Onbekende lead"}</span>
                  <span>{eur(o.totaal_excl)} ex btw</span>
                  {o.geldig_tot && <span>{"geldig tot " + datumNl(o.geldig_tot)}</span>}
                </>}>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <button className="btn bg" onClick={() => afdrukOfferte(o)}>PDF</button>
                  {o.status === "concept" && <button className="btn bg" onClick={() => setBewerk({ ...o })}>Bewerk</button>}
                  {o.status === "concept" && <button className="btn bp" disabled={bezig} onClick={() => zetStatus(o, "verstuurd")}>Markeer verstuurd</button>}
                  {o.status === "verstuurd" && <button className="btn bp" disabled={bezig} onClick={() => zetStatus(o, "geaccepteerd")}>Geaccepteerd</button>}
                  {o.status === "verstuurd" && <button className="btn bd" disabled={bezig} onClick={() => zetStatus(o, "afgewezen")}>Afgewezen</button>}
                  {(o.status === "geaccepteerd" && !o.factuur_doc_id) && <button className="btn bp" disabled={bezig} onClick={() => maakFactuur(o)}>Maak factuur</button>}
                </div>
              </Rij>
            );
          })}
        </div>
      )}

      {tab === "facturen" && !laden && (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          {facturen.length === 0 && <Leeg tekst="Nog geen verkoopfacturen. Een geaccepteerde offerte maakt er met een klik een." />}
          {facturen.map((f) => {
            const s = factuurStatus(f);
            const l = lead(f.lead_id);
            return (
              <Rij key={f.id} titel={<>{f.customer || "Klant"} <span style={{ color: C.muted, fontWeight: 400 }}>{f.factuur_nummer || "geen nummer"}</span></>}
                rechts={<span style={{ display: "inline-flex", gap: 4, flexWrap: "wrap", justifyContent: "flex-end" }}><Chip tekst={s.tekst} kleur={s.kleur} />{f.herinnering_count > 0 && <Chip tekst={f.herinnering_count + "x herinnerd"} />}</span>}
                meta={<>
                  <span>{eur(f.amount_incl)} incl. btw</span>
                  <span>{datumNl(f.doc_date)}</span>
                  {f.vervaldatum && <span>{"vervalt " + datumNl(f.vervaldatum)}</span>}
                  {l && <span style={{ color: C.blue, cursor: "pointer" }} onClick={(e) => { e.stopPropagation(); if (openKlant) openKlant(l.id); }}>dossier</span>}
                </>}>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <button className="btn bg" onClick={() => afdrukFactuur(f)}>PDF</button>
                  {f.payment_status !== "betaald" && !f.payment_url && <button className="btn bp" disabled={bezig} onClick={() => betaalLink(f)}>Betaallink</button>}
                  {f.payment_status !== "betaald" && f.payment_url && <button className="btn bg" onClick={() => { if (navigator.clipboard) navigator.clipboard.writeText(f.payment_url); toon("Betaallink gekopieerd."); }}>Kopieer link</button>}
                  {f.payment_status !== "betaald" && <button className="btn bg" onClick={() => markeerBetaald(f)}>Betaald</button>}
                </div>
              </Rij>
            );
          })}
        </div>
      )}

      {bewerk && (
        <Venster titel={bewerk.id ? "Offerte bewerken" : "Nieuwe offerte"} onSluit={() => setBewerk(null)} breed={720}>
          <Veld label="Klant of lead">
            <select value={bewerk.lead_id} onChange={(e) => setBewerk({ ...bewerk, lead_id: e.target.value })}>
              <option value="">Kies...</option>
              {leads.map((l) => <option key={l.id} value={l.id}>{leadNaam(l)}{l.name && echtBedrijf(l) ? " (" + l.name + ")" : ""}</option>)}
            </select>
          </Veld>
          <Veld label="Titel"><input value={bewerk.titel} onChange={(e) => setBewerk({ ...bewerk, titel: e.target.value })} placeholder="Bijvoorbeeld: Webshop voor bakkerij" /></Veld>
          <Veld label="Regels" hulp='Vink "per maand" aan voor doorlopende zorg. Die regels worden na acceptatie automatisch een abonnement en tellen mee in de MRR.'>
            {bewerk.regels.map((r, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "3fr 70px 110px auto auto", gap: 6, marginBottom: 6, alignItems: "center" }}>
                <input value={r.omschrijving} placeholder="Omschrijving" onChange={(e) => setBewerk({ ...bewerk, regels: bewerk.regels.map((x, j) => j === i ? { ...x, omschrijving: e.target.value } : x) })} />
                <input type="number" min="0" value={r.aantal} onChange={(e) => setBewerk({ ...bewerk, regels: bewerk.regels.map((x, j) => j === i ? { ...x, aantal: e.target.value } : x) })} />
                <input type="number" min="0" step="0.01" value={r.prijs} onChange={(e) => setBewerk({ ...bewerk, regels: bewerk.regels.map((x, j) => j === i ? { ...x, prijs: e.target.value } : x) })} />
                <label style={{ fontSize: 11, color: C.muted, display: "flex", gap: 4, alignItems: "center", whiteSpace: "nowrap" }}>
                  <input type="checkbox" style={{ width: "auto" }} checked={!!r.maand} onChange={(e) => setBewerk({ ...bewerk, regels: bewerk.regels.map((x, j) => j === i ? { ...x, maand: e.target.checked } : x) })} />per maand
                </label>
                <button className="btn bd" onClick={() => setBewerk({ ...bewerk, regels: bewerk.regels.filter((_, j) => j !== i) })} aria-label="Regel verwijderen">x</button>
              </div>
            ))}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
              <button className="btn bg" onClick={() => setBewerk({ ...bewerk, regels: [...bewerk.regels, { ...LEGE_REGEL }] })}>+ Regel</button>
              <select style={{ width: "auto", maxWidth: 260 }} value="" onChange={(e) => {
                const p = catalogus.find((x) => x.slug === e.target.value);
                if (!p) return;
                const nieuw = { omschrijving: p.naam, aantal: 1, prijs: p.prijs, maand: p.type === "doorlopend" };
                const regels = bewerk.regels.length === 1 && !bewerk.regels[0].omschrijving ? [nieuw] : [...bewerk.regels, nieuw];
                setBewerk({ ...bewerk, regels, titel: bewerk.titel || p.naam });
              }}>
                <option value="">+ Pakket uit de catalogus</option>
                {catalogus.map((p) => <option key={p.slug} value={p.slug}>{p.naam} ({eur(p.prijs)}{p.type === "doorlopend" ? " p/m" : ""})</option>)}
              </select>
            </div>
          </Veld>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Veld label="Btw %"><input type="number" value={bewerk.btw_pct} onChange={(e) => setBewerk({ ...bewerk, btw_pct: e.target.value })} /></Veld>
            <Veld label="Geldig tot"><input type="date" value={bewerk.geldig_tot || ""} onChange={(e) => setBewerk({ ...bewerk, geldig_tot: e.target.value })} /></Veld>
          </div>
          <Veld label="Opmerking op de offerte"><textarea rows={2} value={bewerk.notities || ""} onChange={(e) => setBewerk({ ...bewerk, notities: e.target.value })} /></Veld>
          {(() => { const t = berekenTotalen(bewerk.regels, bewerk.btw_pct); return (
            <p style={{ fontSize: 13, margin: "4px 0 14px" }}>
              Eenmalig <strong>{eur(t.excl)}</strong> ex btw, <strong>{eur(t.incl)}</strong> incl.{t.maand > 0 && <> &middot; doorlopend <strong>{eur(t.maand)}</strong> per maand</>}
            </p>); })()}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="btn bg" onClick={() => setBewerk(null)}>Annuleren</button>
            <button className="btn bp" disabled={bezig} onClick={bewaarOfferte}>{bezig ? "Bezig..." : "Opslaan"}</button>
          </div>
        </Venster>
      )}
    </div>
  );
}
